/**
 * Prop Studio — a number box that can be TYPED into.
 *
 * A controlled `<input type="number">` fights the typist: the browser reports
 * "-" (and "1.", and an empty box on the way to "-0.5") as an empty value, the
 * handler ignores it, React writes the old number back, and the minus sign is
 * gone before the next key. So this one keeps the text as typed while the box
 * has focus, commits every state of it that reads as a number the caller
 * accepts (the slider and the viewer follow along), and shows the committed
 * number again when focus leaves. Enter ends the edit; the arrow keys step.
 *
 * Pure React, no three.js: jest can import it (parseDraftNumber is tested).
 */

import React, { useEffect, useRef, useState } from 'react';

/** The number a typed draft reads as, or null while it is not one ("-", "1e", ""). */
export function parseDraftNumber(text: string): number | null {
  // A typed minus may be the real one or the sign the buttons show (−).
  const t = text.trim().replace(/−/g, '-').replace(',', '.');
  if (!/^[-+]?(\d+\.?\d*|\.\d+)$/.test(t)) return null;
  const v = Number(t);
  return Number.isFinite(v) ? v : null;
}

/** Three decimals is as fine as the controls go; it also keeps 0.1 + 0.2 from showing as 0.30000000000000004. */
const format = (v: number): string => String(Math.round(v * 1000) / 1000);

export interface NumberFieldProps {
  value: number;
  onCommit: (value: number) => void;
  /** A number that must not be committed (a scale of 0): the text stays, the value does not move. */
  accept?: (value: number) => boolean;
  /** What the arrow keys step by (default 1). */
  step?: number;
  min?: number;
  max?: number;
  style?: React.CSSProperties;
  'aria-label'?: string;
  title?: string;
}

export const NumberField: React.FC<NumberFieldProps> = ({ value, onCommit, accept, step = 1, min, max, style, title, 'aria-label': ariaLabel }) => {
  // null: not being edited, the box shows `value`.
  const [text, setText] = useState<string | null>(null);
  const lastCommitted = useRef<number | null>(null);

  // The value moved under an edit without going through this box (a slider,
  // a ± button, the viewer's arrow keys): the draft is stale, drop it.
  useEffect(() => {
    if (text !== null && lastCommitted.current !== value) setText(null);
  }, [value, text]);

  const commit = (v: number) => {
    if (accept && !accept(v)) return;
    lastCommitted.current = v;
    onCommit(v);
  };
  const clamp = (v: number) => Math.min(max ?? Infinity, Math.max(min ?? -Infinity, v));

  return (
    <input
      type="text" inputMode="decimal" autoComplete="off" spellCheck={false}
      value={text ?? format(value)} style={style} aria-label={ariaLabel} title={title}
      onFocus={() => { lastCommitted.current = value; setText(format(value)); }}
      onChange={(e) => {
        const raw = e.target.value;
        setText(raw);
        const v = parseDraftNumber(raw);
        if (v !== null) commit(v);
      }}
      onBlur={() => setText(null)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === 'Escape') { e.currentTarget.blur(); return; }
        if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
        e.preventDefault();
        const next = Math.round(clamp(value + (e.key === 'ArrowUp' ? step : -step)) * 1000) / 1000;
        commit(next);
        setText(format(next));
      }}
    />
  );
};

export default NumberField;
